import { useCallback, useEffect, useMemo, useState } from 'react'
import type { SafetyFunctionWithArchitecture } from '@/shared/hooks/api/machineryDirectiveCompliance'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  validateSafetyFunctionForm,
  type SafetyFunctionFormData,
} from './safetyFunctionForm.schema'
import { toSafetyFunctionFormData } from './safetyFunctionsUtils'

type EditSafetyFunctionTextFieldDialogProps = {
  isOpen: boolean
  onClose: () => void
  safetyFunction: SafetyFunctionWithArchitecture
  field: keyof SafetyFunctionFormData
  labelKey: string
  multiline?: boolean
  onSave: (data: SafetyFunctionFormData) => void
  isSaving: boolean
}

export function EditSafetyFunctionTextFieldDialog({
  isOpen,
  onClose,
  safetyFunction,
  field,
  labelKey,
  multiline = false,
  onSave,
  isSaving,
}: EditSafetyFunctionTextFieldDialogProps) {
  const { t } = useTranslation('machineryDirectiveCompliance')
  const [value, setValue] = useState('')

  useEffect(() => {
    if (!isOpen) {
      return
    }
    const formData = toSafetyFunctionFormData(safetyFunction)
    setValue(formData[field])
  }, [isOpen, safetyFunction, field])

  const canSubmit = useMemo(() => {
    const mergedData: SafetyFunctionFormData = {
      ...toSafetyFunctionFormData(safetyFunction),
      [field]: value.trim(),
    }
    return validateSafetyFunctionForm(mergedData).success
  }, [field, safetyFunction, value])

  const handleClose = useCallback(() => {
    onClose()
  }, [onClose])

  const handleSubmit = () => {
    if (!canSubmit || isSaving) {
      return
    }

    onSave({
      ...toSafetyFunctionFormData(safetyFunction),
      [field]: value.trim(),
    })
  }

  const fieldLabel = t(labelKey)

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className='sm:max-w-lg' showCloseButton={!isSaving}>
        <DialogHeader>
          <DialogTitle>
            {t('safetyFunctionsPage.sfDetail.editTextFieldDialog.title', {
              field: fieldLabel,
            })}
          </DialogTitle>
        </DialogHeader>

        <div className='space-y-2'>
          <Label htmlFor={`sf-edit-${field}`}>{fieldLabel}</Label>
          {multiline ? (
            <Textarea
              id={`sf-edit-${field}`}
              value={value}
              onChange={(event) => {
                setValue(event.target.value)
              }}
              rows={5}
            />
          ) : (
            <Input
              id={`sf-edit-${field}`}
              value={value}
              onChange={(event) => {
                setValue(event.target.value)
              }}
            />
          )}
        </div>

        <DialogFooter>
          <Button variant='outline' onClick={handleClose} disabled={isSaving}>
            {t('common:actions.cancel')}
          </Button>
          <Button onClick={handleSubmit} disabled={!canSubmit || isSaving}>
            {t('common:actions.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
